/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import _ from "lodash";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { Button } from "../../components/Button";
import { Loader, LoadingBox } from "../../components/Loader";
import Modal from "../../components/Modal";
import Typography from "../../components/Typography";
import { useAppDispatch } from "../../store/rootStore";
import TestIds from "../../testIds";
import { error as logError } from "../../util/log";
import { useSeedBranch } from "../../util/useBranch";
import useToast from "../../util/useToast";
import { selectCurrentFarmerAccount } from "../azure-b2c/storage";

import { AddPaddockMenu } from "./components/AddPaddockMenu";
import { NewSeedTypeButton } from "./components/NewSeedTypeButton";
import RenameSeedMixProvider from "./components/useRenameSeedMixModal";
import { SeedMixForm } from "./form/SeedMixForm";
import SeedCartSlice, {
  selectSeedCartError,
  selectSeedOrder,
  selectSeedOrderLoading,
  selectSeedOrderSyncing,
} from "./storage/seedCart.slice";
import { getSeedCart, syncSeedCart } from "./storage/seedCartThunk";
import { SeedErrorCode } from "./storage/types";
import { useSeedCart } from "./storage/useSeedCart";
import { SEED_MIX_TYPE, SeedOrderSchema, SeedOrdersInfo } from "./types/SeedOrderFormType";
import { useCheckLocalStorage } from "./utils/checkSeedInLocalStorage";
import { getAllProductIds } from "./utils/getAllProductIds";

export const SeedOrderForm = () => {
  const account = useSelector(selectCurrentFarmerAccount);
  const accountNumber = account?.accountNumber ?? "";
  const { checkSeedInLocalStorage } = useCheckLocalStorage();

  const seedOrder = useSelector(selectSeedOrder(accountNumber));
  const loading = useSelector(selectSeedOrderLoading);
  const syncing = useSelector(selectSeedOrderSyncing);
  const error = useSelector(selectSeedCartError(accountNumber));

  const dispatch = useAppDispatch();
  const { notify } = useToast();

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const { copySeed, addCustomSeed, addPremixSeed, onSubmit } = useSeedCart();

  const validation = SeedOrderSchema.safeParse(seedOrder);

  const blacklistErrors = [
    "This seed mix needs at least 1 product.",
    "This custom seed mix needs at least 2 products.",
  ];

  const seedErrors = validation.error?.formErrors?.fieldErrors?.mixes?.filter(
    (e) => !blacklistErrors.includes(e)
  );

  const branchId = useSeedBranch();

  const [showOutOfDateModal, setShowOutOfDateModal] = React.useState(false);

  useEffect(() => {
    if (accountNumber && error) {
      notify(`Sync Error: ${error}`);
      dispatch(SeedCartSlice.actions.setError({ accountNumber, message: undefined }));
    }
  }, [accountNumber, error]);

  const [synced, setSynced] = React.useState(false);

  useEffect(() => {
    setSynced(false);
  }, [seedOrder]);

  // Get product information and make sure it is available, or the title won't display
  useEffect(() => {
    if (seedOrder?.mixes && seedOrder.accountNumber) {
      const productIds = getAllProductIds(seedOrder.mixes);
      checkSeedInLocalStorage(productIds, seedOrder.accountNumber ?? "", branchId);
    }
  }, [seedOrder]);

  useEffect(() => {
    dispatch(getSeedCart({ accountNumber: accountNumber }));
  }, [accountNumber]);

  const syncSeedOrder = async (seedOrder: SeedOrdersInfo) => {
    if (accountNumber) {
      dispatch(syncSeedCart({ seedOrder, accountNumber }))
        .unwrap()
        .then((result: unknown) => {
          // No result usually meant there was an error so do not proceed
          if (result) {
            setSynced(true);
            toast("Prices updated");
          }
        })
        .catch((e: Error) => {
          if (e.message === SeedErrorCode.InvalidOrder) {
            setShowOutOfDateModal(true);
          } else {
            toast(e.message);
          }
        });
    }
  };

  const ordersRef = useRef(seedOrder);

  const [formInvalidated, setFormInvalidated] = React.useState<string[]>([]);

  useEffect(() => {
    if (seedOrder && account?.accountNumber) {
      if (!_.isEqual(ordersRef.current, seedOrder) || ordersRef.current === undefined) {
        const prevLength = ordersRef.current?.mixes.length ?? 0;
        ordersRef.current = seedOrder;
        dispatch(
          syncSeedCart({
            accountNumber: account.accountNumber,
            seedOrder: seedOrder,
          })
        )
          .unwrap()
          .then((value) => {
            if (value) {
              if (seedOrder.mixes.length === 0 && prevLength !== 0) {
                toast("Seed order removed");
              } else if (prevLength < seedOrder.mixes.length) {
                toast("Mix added successfully");
              } else if (prevLength > seedOrder.mixes.length) {
                toast("Mix removed successfully");
              }
            }
          })
          .catch((err) => {
            logError(err);
            toast(err);
          });
      }
    }
  }, [seedOrder?.mixes.length]);

  const loader = React.useMemo(
    () => (
      <LoadingBox pt={3}>
        <Loader type="inline-flex" title="Loading..." />
      </LoadingBox>
    ),
    []
  );
  if (!seedOrder) {
    return;
  }

  if (loading) {
    return loader;
  }

  return (
    // <FormProvider {...useFormApi}>
    <RenameSeedMixProvider>
      <Modal
        open={showOutOfDateModal}
        handleClose={() => setShowOutOfDateModal(false)}
        title="Order out of date"
        footer={
          <Button
            sx={{ width: isSm ? "100%" : "auto" }}
            variant="contained"
            onClick={() => setShowOutOfDateModal(false)}
          >
            Okay
          </Button>
        }
      >
        <Typography variant="body2" gutterBottom>
          This order was updated from another device. The latest changes have replaced your order.
          Please review before continuing.
        </Typography>
      </Modal>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <fieldset style={{ border: "none" }} disabled={syncing}>
          {seedOrder.mixes.length === 0 ? (
            <Stack
              direction={isSm ? "column" : "row"}
              justifyContent="center"
              alignItems="center"
              spacing={2}
            >
              <NewSeedTypeButton seedType={SEED_MIX_TYPE.PREMIX} onClick={() => addPremixSeed()} />
              <NewSeedTypeButton seedType={SEED_MIX_TYPE.CUSTOM} onClick={() => addCustomSeed()} />
            </Stack>
          ) : null}
          <Stack spacing={4}>
            {seedOrder.mixes.map((mix) => {
              return (
                <SeedMixForm
                  addCustomSeed={addCustomSeed}
                  addPremixSeed={addPremixSeed}
                  copySeed={copySeed}
                  formInvalidated={formInvalidated.includes(mix.id ?? mix.name)}
                  seedMix={mix}
                  key={mix.id ?? mix.name}
                />
              );
            })}
          </Stack>
          {seedErrors && seedOrder?.mixes?.length && seedErrors.length ? (
            <Typography
              variant="body2"
              color="error700"
              mt={3}
              data-testid={TestIds.feat.seed.notEnoughSeedsError}
            >
              {seedErrors.map((error) => (
                <>
                  <span key={error}>{error}</span>
                  <br />
                </>
              ))}
            </Typography>
          ) : null}
          {seedOrder.mixes.length > 0 && (
            <Stack direction="row" justifyContent="flex-end">
              <Box sx={{ width: isSm ? "100%" : "auto", mt: 3, mb: 1 }}>
                {syncing ? (
                  <LoadingBox>
                    <Loader title="Syncing your cart" type="inline-flex" />
                  </LoadingBox>
                ) : (
                  <Stack direction={isSm ? "column" : "row"} spacing={1}>
                    <AddPaddockMenu onAddCustom={addCustomSeed} onAddPremix={addPremixSeed} />
                    {!synced && (
                      <Button
                        variant="contained"
                        onClick={() => {
                          const validated = SeedOrderSchema.safeParse(seedOrder);
                          if (validated.success) {
                            syncSeedOrder(validated.data);
                            setFormInvalidated([]);
                          } else {
                            setFormInvalidated(seedOrder.mixes.map((mix) => mix.id ?? mix.name));
                            toast("Form not valid");
                          }

                          if (seedOrder.mixes.length === 0) {
                            syncSeedOrder(seedOrder);
                          }
                        }}
                        data-testid={TestIds.feat.seedOrders.footer.calculatePrices}
                      >
                        Calculate prices
                      </Button>
                    )}
                    {synced && (
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={syncing}
                        data-testid={TestIds.feat.seedOrders.footer.continue}
                      >
                        Continue
                      </Button>
                    )}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </fieldset>
      </form>
    </RenameSeedMixProvider>
    // </FormProvider>
  );
};
